import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PointForm from "../components/PointForm";
import { useFetchWithToken } from "../auth/authorization";

export default function EditPoint() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const apiBase = process.env.REACT_APP_SERVER_API_URL || "";
    const base = apiBase.replace(/\/$/, "");

    // previous page to return to (set by callers via navigation state)
    const returnTo = location.state?.from ?? "/manage";

    const [point, setPoint] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchWithToken = useFetchWithToken();

    useEffect(() => {
        let cancelled = false;
        async function fetchPoint() {
            setLoading(true);
            try {
                // Try single-point endpoint first
                let res = await fetch(`${base}/api/points/${encodeURIComponent(id)}`);
                let data = null;
                if (res.ok) {
                    data = await res.json().catch(() => null);
                } else {
                    // fallback: fetch all and find
                    res = await fetch(`${base}/api/points`);
                    if (!res.ok) throw new Error(`HTTP ${res.status}`);
                    const all = await res.json();
                    data = Array.isArray(all) ? all.find(p => String(p.id) === String(id)) : null;
                }

                if (cancelled) return;
                if (!data) {
                    setError("Point not found.");
                } else {
                    setPoint(data);
                }
            } catch (err) {
                console.error("Failed to load point:", err);
                if (!cancelled) setError(err instanceof Error ? err.message : String(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        fetchPoint();
        return () => { cancelled = true; };
    }, [base, id]);

    async function handleSave(payload) {
        if (!point) return;
        setSaving(true);
        setError(null);
        try {
            // ensure Id is included for edit
            const body = { ...payload, Id: point.id };
            const res = await fetchWithToken(`${base}/api/point/edit`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            });
            const data = await res.json().catch(() => null);
            if (!res.ok) {
                const msg = (data && (data.message || data.Message)) || `HTTP ${res.status}`;
                throw new Error(msg);
            }
            // go back to the caller (Manage or View)
            navigate(returnTo);
        } catch (err) {
            console.error("Failed to save point:", err);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setSaving(false);
        }
    }

    if (loading) return <div className="container py-4">Loading�</div>;
    if (error) return <div className="container py-4"><div className="alert alert-danger">{error}</div></div>;
    if (!point) return <div className="container py-4"><div className="alert alert-warning">Point not found.</div></div>;

    return (
        <main className="container py-4" style={{ maxWidth: 800 }}>
            <PointForm
                initialData={point}
                title="EDIT POINT"
                submitLabel="Save"
                saving={saving}
                externalError={error}
                onSubmit={handleSave}
                onCancel={() => navigate(returnTo)}
            />
        </main>
    );
}